import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { UseDataProps } from '10.quickConnect.app/components/shared/Dialogs/BackupDeclarationDialog/type';
import { countSavedFields } from '10.quickConnect.app/components/shared/Dialogs/BackupDeclarationDialog/helpers';
import { DeclarationLocationState } from '10.quickConnect.app/components/domain/Declaration/types';
import { useStore } from '30.quickConnect.Stores';
import { DECLARATION_FROM_INBOX, NEW_DECLARATION } from '10.quickConnect.app/routes/paths';
import { LocalBackupDeclaration } from '90.quickConnect.Models/models';
import { errorHandler } from '80.quickConnect.Core/helpers';

const useData = (): UseDataProps => {
  // Tag
  const tag = '10.quickConnect.app/components/shared/Dialogs/BackupDeclarationDialog/hooks.tsx';

  const navigate = useNavigate();
  const { t } = useTranslation('forms');

  const {
    DeclarationStore: { backupDclContext, discardBackupDeclarationAsync, removeFromBackupDclContext },
  } = useStore();

  // Les saisies interrompues sont proposées une à une
  const [unsavedEntry] = backupDclContext;

  // Dernière saisie proposée, affichée pendant l'animation de fermeture de la popup
  const displayedEntryRef = useRef<LocalBackupDeclaration>();
  if (unsavedEntry) displayedEntryRef.current = unsavedEntry;

  // Garde contre le double-clic pendant l'abandon
  const isDiscardingRef = useRef<boolean>(false);

  const onResumeDeclaration = useCallback(() => {
    if (!unsavedEntry?.id) return;

    const { id, formId, inboxId, activityId } = unsavedEntry;
    removeFromBackupDclContext(id);

    // Saisie ouverte depuis la boîte de réception : elle est reprise comme si l'élément y était rouvert
    if (inboxId && activityId) {
      const link = DECLARATION_FROM_INBOX.replace(':formId', formId)
        .replace(':inboxId', inboxId)
        .replace(':activityId', activityId);
      navigate(link, { state: { activityId, backupDeclarationId: id } });
      return;
    }

    const state: DeclarationLocationState = { backupDeclarationId: id };
    navigate(NEW_DECLARATION.replace(':formId', formId), { state });
  }, [unsavedEntry, removeFromBackupDclContext, navigate]);

  const onDiscardDeclaration = useCallback(async () => {
    if (!unsavedEntry?.id || isDiscardingRef.current) return;

    isDiscardingRef.current = true;
    try {
      const isDiscarded = await discardBackupDeclarationAsync(unsavedEntry.id);

      if (isDiscarded) toast.success(t('qcapp_form_choice_dialog_success_one').toString());
      else toast.warn(t('qcapp_declaration_opened_elsewhere').toString());
    } catch (error: unknown) {
      errorHandler(tag, error, 'onDiscardDeclaration');
      toast.error(t('qcapp_form_choice_dialog_error').toString());
    } finally {
      isDiscardingRef.current = false;
    }
  }, [unsavedEntry, discardBackupDeclarationAsync, t, tag]);

  const displayedEntry = displayedEntryRef.current;
  const unsavedEntryLabel = displayedEntry
    ? t('qcapp_back_up_unsaved_entry_item', {
        name: displayedEntry.name,
        fieldCount: countSavedFields(displayedEntry.editedData),
      }).toString()
    : '';

  return {
    onResumeDeclaration,
    onDiscardDeclaration,
    unsavedEntryLabel,
    openModal: !!unsavedEntry,
    t,
  };
};

export default useData;
